import { useEffect, useState } from 'react';
import type { Order } from '@/types/order';
import { formatOrderTotalPrice, type InvoiceCloseAction, type OrderPaymentMethod } from '@/api/modules/order';
import { EnglishDateInput } from '@/components/ui/EnglishDateInput';
import { formatGstPercentInput, orderGstRateToDecimal } from '@/lib/orderPricing';

export interface ConfirmPaymentCloseDetails {
  paymentMethod?: OrderPaymentMethod;
  paymentDate?: string;
}

interface ConfirmPaymentCloseModalProps {
  order: Order | null;
  action: InvoiceCloseAction | null;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (details?: ConfirmPaymentCloseDetails) => void;
}

export function invoiceCloseActionLabel(action: InvoiceCloseAction): string {
  if (action === 'close') return 'Close Order';
  if (action === 'pay_and_close') return 'Confirm & Close';
  return 'Confirm Payment';
}

function actionCopy(action: InvoiceCloseAction): { title: string; body: string; confirm: string } {
  if (action === 'pay') {
    return {
      title: 'Confirm Payment',
      body: 'Mark this invoice as paid. The order is already closed.',
      confirm: 'Confirm Payment',
    };
  }
  if (action === 'close') {
    return {
      title: 'Close Order',
      body: 'Payment is already recorded. Close this order and finish it.',
      confirm: 'Close Order',
    };
  }
  return {
    title: 'Confirm & Close',
    body: 'Choose the payment date and method, then confirm. The order will be completed and closed, and assigned staff split the hours equally.',
    confirm: 'Confirm & Close',
  };
}

function todayIso(): string {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function orderTransferCode(orderId: string): string {
  const trimmed = orderId.trim();
  return trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
}

export default function ConfirmPaymentCloseModal({
  order,
  action,
  submitting,
  onClose,
  onConfirm,
}: ConfirmPaymentCloseModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod | ''>('');
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [copied, setCopied] = useState(false);
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    setPaymentMethod('');
    setPaymentDate(todayIso());
    setCopied(false);
    setDetailError('');
  }, [order?.id, action]);

  if (!order || !action) return null;
  const copy = actionCopy(action);
  const fee = typeof order.totalPriceWithGst === 'number' ? order.totalPriceWithGst : order.totalPrice;
  const gstDecimal = orderGstRateToDecimal(order.gstRate);
  const gstLabel = gstDecimal === null ? null : `${formatGstPercentInput(gstDecimal)}%`;
  const transferCode = orderTransferCode(order.id);
  const canRecordPayment = action === 'pay_and_close';

  const copyTransferCode = async () => {
    try {
      await navigator.clipboard.writeText(transferCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const confirmWithDetails = () => {
    if (!paymentMethod) {
      setDetailError('Select Transfer or Cash.');
      return;
    }
    if (!paymentDate) {
      setDetailError('Select the payment date.');
      return;
    }
    setDetailError('');
    onConfirm({ paymentMethod, paymentDate });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-[430px] bg-white rounded-t-2xl px-4 pt-4 pb-6 shadow-xl max-h-[88vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-bold text-gray-900">{copy.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">{copy.body}</p>

        <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 mb-4 space-y-2">
          <div className="flex items-center justify-between text-sm gap-3">
            <span className="text-gray-500">Order</span>
            <span className="font-semibold text-gray-900 tabular-nums">#{transferCode}</span>
          </div>
          {canRecordPayment ? (
            <div className="flex items-center justify-between text-sm gap-3">
              <span className="text-gray-500">Transfer code</span>
              <button
                type="button"
                onClick={() => void copyTransferCode()}
                className="font-semibold text-gray-900 tabular-nums inline-flex items-center gap-1 cursor-pointer"
              >
                {transferCode}
                <i className={`${copied ? 'ri-check-line text-green-600' : 'ri-file-copy-line text-gray-400'} text-sm`} />
              </button>
            </div>
          ) : null}
          <div className="flex items-center justify-between text-sm gap-3">
            <span className="text-gray-500">Amount</span>
            <span className="font-semibold text-gray-900 tabular-nums">{formatOrderTotalPrice(fee)}</span>
          </div>
          {gstLabel ? (
            <div className="flex items-center justify-between text-sm gap-3">
              <span className="text-gray-500">GST</span>
              <span className="font-semibold text-gray-900 tabular-nums">{gstLabel}</span>
            </div>
          ) : null}
        </div>

        {canRecordPayment ? (
          <div className="space-y-3">
            <p className="text-[11px] text-gray-400 leading-relaxed">
              The order number is the customer transfer code. Choose the payment date and how it was received.
            </p>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Payment date</div>
              <EnglishDateInput
                id="payment-arrival-date"
                value={paymentDate}
                onChange={(next) => {
                  setPaymentDate(next);
                  setDetailError('');
                }}
                pickerTitle="Payment date"
              />
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Payment method</div>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { id: 'transfer', label: 'Transfer' },
                  { id: 'cash', label: 'Cash' },
                ] as const).map((option) => {
                  const selected = paymentMethod === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setPaymentMethod(option.id);
                        setDetailError('');
                      }}
                      className={`h-9 rounded-lg border text-sm font-medium cursor-pointer ${
                        selected
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-gray-200 bg-white text-gray-600'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {detailError ? (
              <p className="text-[11px] text-red-500">{detailError}</p>
            ) : null}
            <button
              type="button"
              disabled={submitting}
              onClick={confirmWithDetails}
              className="w-full h-11 rounded-xl bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Confirm & Close'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={submitting}
            onClick={() => onConfirm()}
            className="w-full h-11 rounded-xl bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? 'Saving…' : copy.confirm}
          </button>
        )}
      </div>
    </div>
  );
}
